import { useState } from "react";
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent,
  Button, 
  Badge, 
  Input,
  Select, 
  Textarea,
  StatusPill,
  StatCard,
  HeaderMark,
  BarList,
  ClientHubBadge,
  PageContentRenderer,
  PlanBadge,
  AuthMethodsSeparator,
  PartnerStatusBadgeWithTooltip,
  CommissionStatusBadges
} from "@repo/o37-ui";

// Mock Data
const sampleBlocks = [
  { id: "1", type: "text", data: { markdown: "### Block Content\nThis section demonstrates the `PageContentRenderer` component which handles various content blocks." } },
  { id: "2", type: "alert", data: { message: "This is an alert block demonstrating an info message.", type: "info" } },
  { id: "3", type: "metrics", data: { metrics: [{ label: "Sample Metric", value: "100%", subtext: "Growth" }, { label: "Active Users", value: "50k" }] } }
];

const plans = ["free", "pro", "business", "business plus", "enterprise"];

// Tabs Component
function Tabs({ tabs, activeTab, onChange }: { tabs: string[], activeTab: string, onChange: (t: string) => void }) {
  return (
    <div className="flex border-b border-neutral-200 mb-6 overflow-x-auto">
      {tabs.map((tab) => (
        <button
          key={tab}
          onClick={() => onChange(tab)}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === tab
              ? "border-black text-black"
              : "border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300"
          }`}
        >
          {tab}
        </button>
      ))}
    </div>
  );
}

export default function UI() {
  const [activeTab, setActiveTab] = useState("Core");
  const tabs = ["Core", "Auth", "Partners", "Workspaces", "Analytics", "Blocks"];

  return (
    <div className="w-full p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold mb-2">UI Component Gallery</h1>
        <p className="text-neutral-500">Comprehensive overview of shared UI components.</p>
      </div>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {/* CORE COMPONENTS */}
      {activeTab === "Core" && (
        <div className="space-y-10 animate-in fade-in duration-500">
          <section>
            <h2 className="text-lg font-medium mb-4">Buttons</h2>
            <div className="flex flex-wrap gap-4 p-4 border rounded-lg bg-neutral-50">
              <Button text="Default Primary" />
              <Button text="Secondary" variant="secondary" />
              <Button text="Outline" variant="outline" />
              <Button text="Destructive" variant="danger" />
              <Button text="Loading" loading />
              <Button text="Disabled" disabled />
            </div>
          </section>

          <section>
            <h2 className="text-lg font-medium mb-4">Badges & Status</h2>
            <div className="grid gap-6">
              <div className="p-4 border rounded-lg bg-white space-y-4">
                <h3 className="text-sm text-neutral-500 uppercase font-semibold">Standard Badges</h3>
                <div className="flex flex-wrap gap-3">
                  <Badge>Default</Badge>
                  <Badge variant="secondary">Gray</Badge>
                  <Badge variant="secondary">Neutral</Badge>
                  <Badge variant="secondary">Blue</Badge>
                  <Badge variant="success">Green</Badge>
                  <Badge variant="warning">Yellow</Badge>
                  <Badge variant="danger">Red</Badge>
                </div>
              </div>

              <div className="p-4 border rounded-lg bg-white space-y-4">
                <h3 className="text-sm text-neutral-500 uppercase font-semibold">Priority Badges</h3>
                <div className="flex flex-wrap gap-3">
                  {/* <PriorityBadge priority="high" /> */}
                  {/* <PriorityBadge priority="medium" /> */}
                  {/* <PriorityBadge priority="low" /> */}
                </div>
              </div>

              <div className="p-4 border rounded-lg bg-white space-y-4">
                <h3 className="text-sm text-neutral-500 uppercase font-semibold">Client Hub Badges</h3>
                <div className="flex flex-wrap gap-3">
                  <ClientHubBadge status="synced" />
                  <ClientHubBadge status="pending" />
                  <ClientHubBadge status="failed" />
                  <ClientHubBadge status="not_synced" />
                </div>
              </div>

               <div className="p-4 border rounded-lg bg-white space-y-4">
                <h3 className="text-sm text-neutral-500 uppercase font-semibold">Status Pills</h3>
                <div className="flex flex-wrap gap-3">
                  <StatusPill s="active" />
                  <StatusPill s="pending" />
                  {/* <StatusPill status="inactive" /> */}
                  {/* <StatusPill status="error" /> */}
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-medium mb-4">Form Inputs</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl p-4 border rounded-lg bg-neutral-50">
              <div className="space-y-2">
                <label className="text-sm font-medium">Standard Input</label>
                <Input placeholder="Type something..." />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Select Dropdown</label>
                <Select>
                    <option value="1">Option One</option>
                    <option value="2">Option Two</option>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium">Textarea</label>
                <Textarea placeholder="Type a longer description here..." className="min-h-[100px]" />
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-medium mb-4">Cards & Containers</h2>
             <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>Standard Card</CardTitle>
                </CardHeader>
                <CardContent>
                  Basic content container with padding.
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Stat Card Example</CardTitle>
                </CardHeader>
                <CardContent>
                   <StatCard label="Total Revenue" value="$12,345" />
                </CardContent>
              </Card>
               <Card className="flex items-center justify-center p-6 bg-neutral-900 text-white">
                 <HeaderMark className="text-white" />
              </Card>
            </div>
          </section>
        </div>
      )}

      {/* AUTH COMPONENTS */}
      {activeTab === "Auth" && (
        <div className="space-y-10 animate-in fade-in duration-500">
           <section>
             <h2 className="text-lg font-medium mb-4">Authentication UI</h2>
             <Card className="max-w-md mx-auto">
               <CardHeader>
                 <CardTitle>Login Preview</CardTitle>
               </CardHeader>
               <CardContent className="space-y-4">
                  {/* Note: Functional components like GoogleButton are excluded as they depend on Next.js context */}
                  <div className="text-center text-sm text-neutral-500 mb-4">
                    Social buttons not available in preview mode (Next.js dependency)
                  </div>
                  <AuthMethodsSeparator />
                  <div className="text-sm font-medium mb-1">Email Sign In (UI Only)</div>
                  {/* Mocking the EmailSignIn look since the real one has Next.js hooks */}
                  <div className="space-y-4">
                     <Input placeholder="name@example.com" />
                     <Button text="Sign In with Email" className="w-full" />
                  </div>
               </CardContent>
             </Card>
           </section>
        </div>
      )}

      {/* PARTNERS COMPONENTS */}
      {activeTab === "Partners" && (
        <div className="space-y-10 animate-in fade-in duration-500">
          <section>
            <h2 className="text-lg font-medium mb-4">Partner Status Badges</h2>
            <div className="p-4 border rounded-lg bg-neutral-50 flex flex-wrap gap-4">
                {['approved', 'pending', 'rejected', 'banned', 'deactivated'].map((status) => (
                    <div key={status} className="flex flex-col items-center gap-2">
                        <span className="text-xs text-neutral-400 capitalize">{status}</span>
                        {/* @ts-ignore - safe mock props */}
                        <PartnerStatusBadgeWithTooltip partner={{ status }} />
                    </div>
                ))}
            </div>
          </section>

          <section>
            <h2 className="text-lg font-medium mb-4">Commission Statuses</h2>
            <div className="p-4 border rounded-lg bg-neutral-50 grid grid-cols-2 md:grid-cols-4 gap-4">
                 {Object.entries(CommissionStatusBadges).map(([key, badge]: [string, any]) => {
                     const Icon = badge.icon;
                     return (
                         <div key={key} className={`flex items-center gap-2 px-3 py-1.5 rounded-full border border-neutral-200 bg-white w-fit`}>
                             {Icon && <Icon className={`size-4 ${badge.className?.split(" ")[0]}`} />}
                             <span className="text-sm font-medium">{badge.label}</span>
                         </div>
                     )
                 })}
            </div>
          </section>
        </div>
      )}

      {/* WORKSPACES COMPONENTS */}
      {activeTab === "Workspaces" && (
        <div className="space-y-10 animate-in fade-in duration-500">
          <section>
            <h2 className="text-lg font-medium mb-4">Plan Badges</h2>
            <div className="p-6 border rounded-lg bg-neutral-50 flex flex-wrap gap-4 items-center">
                {plans.map(plan => (
                    <div key={plan} className="flex flex-col gap-2 items-center">
                        <PlanBadge plan={plan as any} />
                        <span className="text-xs text-neutral-400">{plan}</span>
                    </div>
                ))}
            </div>
          </section>

          <section>
            <h2 className="text-lg font-medium mb-4">Workspace Actions</h2>
             <div className="p-6 border rounded-lg bg-neutral-50 flex items-center gap-4">
                {/* Note: CreateWorkspaceButton depends on SWR/Modal context, so we show a mock */}
                <Button text="Create Workspace (Preview)" />
             </div>
          </section>
        </div>
      )}

       {/* ANALYTICS COMPONENTS */}
       {activeTab === "Analytics" && (
        <div className="space-y-10 animate-in fade-in duration-500">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card>
                <CardHeader>
                    <CardTitle>Bar List Chart</CardTitle>
                </CardHeader>
                <CardContent>
                    <BarList 
                        tab="Sources"
                        barBackground="bg-neutral-200"
                        hoverBackground="bg-neutral-100"
                        setShowModal={() => {}}
                        data={[
                            { value: 456, icon: null, title: "Direct" },
                            { value: 351, icon: null, title: "Google" },
                            { value: 271, icon: null, title: "Twitter" },
                            { value: 190, icon: null, title: "LinkedIn" },
                        ]}
                    />
                </CardContent>
            </Card>
            
            {/* <Card>
                <CardHeader>
                    <CardTitle>Funnel Chart</CardTitle>
                </CardHeader>
                <CardContent>
                    {/* <FunnelChart 
                        data={[
                            { name: "Impressions", value: 5000 },
                            { name: "Clicks", value: 2500 },
                            { name: "Signups", value: 500 },
                            { name: "Paid", value: 100 },
                        ]}
                    /> * /}
                </CardContent>
            </Card> */}
          </div>
        </div>
      )}

      {/* BLOCKS COMPONENTS */}
      {activeTab === "Blocks" && (
        <div className="space-y-10 animate-in fade-in duration-500">
           <section>
            <h2 className="text-lg font-medium mb-4">Page Content Renderer</h2>
            <div className="border p-6 rounded-lg bg-neutral-50">
                <PageContentRenderer content={sampleBlocks as any} />
            </div>
          </section>
        </div>
      )}

    </div>
  );
}
